import React, { ElementType, JSX } from "react";
import { TextField as MuiTextField, InputAdornment } from "@mui/material";
import { ExpandMoreOutlined } from "@mui/icons-material";
import { useTextFieldStyles } from "./css";

export interface ITextFieldProps {
  name?: string;
  label?: string;
  disabled?: boolean;
  helperText?: string;
  onChange?: React.FormEventHandler<HTMLElement>;
  onFocus?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  placeholder?: string;
  type?: string;
  value?: string | number;
  autoComplete?: "current-password" | "username" | "off" | "new-password";
  required?: boolean;
  margin?: "none" | "dense" | "normal" | undefined;
  endAdornment?: JSX.Element;
  startAdornment?: JSX.Element;
  fullWidth?: boolean;
  select?: boolean;
  error?: boolean;
  children?: React.ReactNode;
  onKeyDown?: (
    event: React.KeyboardEvent<HTMLInputElement> & { target: HTMLInputElement },
  ) => void;
  multiline?: boolean;
  InputProps?: {};
  InputLabelProps?: { shrink?: boolean };
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  selectIcon?: ElementType;
  variant?: "outlined" | "filled" | "standard";
  autoFocus?: boolean;
  fillColor?: "grey" | "green" | "white";
}

const TextField = React.forwardRef<any, ITextFieldProps>((props, ref) => {
  const {
    disabled = false,
    helperText: userHelperText,
    endAdornment: icon,
    startAdornment,
    fullWidth = true,
    select,
    error,
    label,
    value,
    children,
    onChange,
    onFocus,
    type,
    autoComplete,
    margin,
    InputProps,
    variant,
    selectIcon,
    fillColor,
    ...rest
  } = props;
  const { classes, cx } = useTextFieldStyles();
  const endIcon = icon ? (
    <InputAdornment position="end">
      <div>{icon}</div>
    </InputAdornment>
  ) : null;

  const startIcon = startAdornment ? (
    <InputAdornment position="start">{startAdornment}</InputAdornment>
  ) : null;

  return (
    <MuiTextField
      disabled={disabled}
      error={error}
      helperText={userHelperText}
      fullWidth={fullWidth}
      slotProps={{
        input: {
          ...InputProps,
          startAdornment: startIcon,
          endAdornment: endIcon,
        },
        select: {
          IconComponent: selectIcon ? selectIcon : ExpandMoreOutlined,
        },
      }}
      classes={{
        root: cx(classes.root, {
          [classes.greyFilled]: fillColor === "grey",
          [classes.greenFilled]: fillColor === "green",
          [classes.whiteFilled]: fillColor === "white",
        }),
      }}
      variant={variant ? variant : "outlined"}
      size="small"
      select={select}
      label={label}
      value={value}
      inputRef={ref}
      onFocus={onFocus}
      onChange={onChange}
      type={type}
      autoComplete={autoComplete}
      margin={margin}
      {...rest}
    >
      {children}
    </MuiTextField>
  );
});

export default React.memo(TextField);
